import {
  ArrowRight,
  ChevronDown,
  CircleDot,
  Menu,
  TicketPercent,
  X
} from "lucide-react";
import { useEffect, useState, type CSSProperties } from "react";
import { AgentDemo } from "./components/AgentDemo";
import { AmbientOrbit } from "./components/AmbientOrbit";
import { DashboardExtensionPreview } from "./components/DashboardExtensionPreview";
import { DashboardPreview } from "./components/DashboardPreview";
import { HeroApplicationStorm } from "./components/HeroApplicationStorm";
import { OffrLogo } from "./components/OffrLogo";
import { PortalRail } from "./components/PortalRail";
import { RepeatPain } from "./components/RepeatPain";
import { ScrollToTop } from "./components/ScrollToTop";
import { WaitlistForm } from "./components/WaitlistForm";
import { WaitlistTally } from "./components/WaitlistTally";
import { usePageMotion } from "./hooks/usePageMotion";

const faqs = [
  {
    question: "What exactly does Offr do?",
    answer:
      "Offr is a pre-launch job-application agent. The planned product uses verified information from your saved profile to fill external forms, draft role-specific answers in your writing style, prepare an ATS-aware resume, and wait for your approval before submission."
  },
  {
    question: "Does Offr submit applications without me?",
    answer:
      "No. The planned workflow is attended and sequential. Offr prepares one application at a time, surfaces anything uncertain, and stops for your review before submission."
  },
  {
    question: "Will it work on every job portal?",
    answer:
      "Offr is being built for external employer and ATS application pages worldwide, with additional support for India-heavy requirements and systems such as Darwinbox and PeopleStrong. Known layouts use direct mappings, while unfamiliar portals are read by context. Coverage testing is still in progress."
  },
  {
    question: "What about LinkedIn Easy Apply and Naukri?",
    answer:
      "Those platforms can help you discover roles, but their native application flows are outside Offr’s automation boundary. Offr is focused on external employer and ATS application pages."
  },
  {
    question: "What do early waitlist members receive?",
    answer:
      "Early access, product updates, and a genuine founding-member discount. The amount, duration, and eligibility will be disclosed before launch—never hidden behind fake urgency."
  },
  {
    question: "What information does Offr store?",
    answer:
      "Only what you save to your profile — your verified facts, base resume, and writing samples. You control what goes in, and you can remove it at any time."
  }
] as const;

const advantages = [
  {
    title: "10× the volume",
    copy: "Queue the roles you choose; Offr prepares each in sequence."
  },
  {
    title: "Consistent everywhere",
    copy: "Your education, experience, and skills stay identical across every form."
  },
  {
    title: "Sounds like you",
    copy: "Offr drafts from your own writing, not generic AI filler."
  },
  {
    title: "Tailored per role",
    copy: "A factual, role-specific resume for each application."
  },
  {
    title: "Offr asks instead of guessing",
    copy: "Uncertain fields are flagged for you. Nothing submits without your approval."
  },
  {
    title: "Works on portals it has never seen",
    copy: "Known layouts get direct mappings. Anything unfamiliar, Offr reads by context."
  }
] as const;

const solutionLines = [
  "Offr is an AI agent that applies to jobs for you.",
  "You open the roles you want.",
  "Offr fills every form from your saved profile,",
  "writes answers in your voice, tailors your resume for each role,",
  "and stops for your approval before submitting."
] as const;

function SolutionReadingCopy() {
  let characterOrder = 0;
  const fullCopy = solutionLines.join(" ");

  return (
    <p className="solution-pause__copy">
      <span className="sr-only">{fullCopy}</span>
      {solutionLines.map((line, lineIndex) => {
        const words = line.split(" ");
        const renderedWords = words.map((word, wordIndex) => (
          <span className="solution-pause__word" key={`${lineIndex}-${wordIndex}`}>
            {Array.from(word).map((letter, letterIndex) => {
              const order = characterOrder++;

              return (
                <span
                  className="solution-pause__letter"
                  key={`${lineIndex}-${wordIndex}-${letterIndex}`}
                  style={{ "--char-order": order } as CSSProperties}
                >
                  {letter}
                </span>
              );
            })}
            {wordIndex < words.length - 1 ? " " : null}
          </span>
        ));

        characterOrder += 12;

        return (
          <span className="solution-pause__line" aria-hidden="true" key={line}>
            {renderedWords}
          </span>
        );
      })}
    </p>
  );
}

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const isScrolled = usePageMotion();

  const closeMenu = () => setMenuOpen(false);

  useEffect(() => {
    document.documentElement.classList.toggle("nav-drawer-open", menuOpen);
    if (!menuOpen) return;

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    const desktopQuery = window.matchMedia("(min-width: 1081px)");
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };

    window.addEventListener("keydown", closeOnEscape);
    desktopQuery.addEventListener("change", closeAtDesktop);

    return () => {
      document.documentElement.classList.remove("nav-drawer-open");
      window.removeEventListener("keydown", closeOnEscape);
      desktopQuery.removeEventListener("change", closeAtDesktop);
    };
  }, [menuOpen]);

  useEffect(() => {
    const cleanVisibleUrl = () => {
      window.history.replaceState(
        window.history.state,
        "",
        `${window.location.pathname}${window.location.search}`
      );
    };

    const scrollToHash = (hash: string, behavior: ScrollBehavior) => {
      const target = document.getElementById(decodeURIComponent(hash.slice(1)));
      if (!target) return false;

      target.scrollIntoView({ behavior, block: "start" });
      cleanVisibleUrl();
      return true;
    };

    const handleAnchorNavigation = (event: MouseEvent) => {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        !(event.target instanceof Element)
      ) {
        return;
      }

      const anchor = event.target.closest<HTMLAnchorElement>('a[href^="#"]');
      const hash = anchor?.getAttribute("href");
      if (!hash || hash === "#") return;

      const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto"
        : "smooth";

      if (scrollToHash(hash, behavior)) event.preventDefault();
    };

    document.addEventListener("click", handleAnchorNavigation);

    const initialHash = window.location.hash;
    const initialHashTimer = initialHash
      ? window.setTimeout(() => {
          cleanVisibleUrl();
          window.scrollTo({ top: 0, left: 0, behavior: "auto" });
        }, 120)
      : 0;

    return () => {
      window.clearTimeout(initialHashTimer);
      document.removeEventListener("click", handleAnchorNavigation);
    };
  }, []);

  return (
    <div className="site-shell">
      <div className="page-lines" aria-hidden="true" />
      {/*
      impeccable:
      THESIS: Offr turns repetitive job applications into one calm, visible, reviewable queue; refuse the category habit of promising magic above an anonymous dashboard.
      OWN-WORLD: one continuous cobalt-glass field, restrained cyan and violet light, deep navy ink, slim translucent navigation, quiet white product surfaces, and a precise Jakarta/Manrope type pairing.
      STORY: recognise the repeated-form pain, understand Offr in plain language, watch it work sequentially, then see its global portal support, dashboard, and attended controls before joining early access.
      FIRST VIEWPORT: centered floating navigation and promise over a deliberately blurred dashboard/extension stack; scroll resolves the synthetic product scene into focus before releasing the next section.
      FORM: Cobalt Glass, a restrained AI-SaaS material system over the approved Balanced Launchpad composition; the supplied blue-glass image is used directly as the atmospheric source.
      FINISH: unreviewed and undocumented is unfinished; this build ends with the finish review, the verdict, DESIGN.md, and every shipping raster carrying its provenance
      */}

      <header className={`site-header ${isScrolled ? "is-compact" : ""}`}>
        <div className="site-header__identity">
          <a className="brand" href="#top" aria-label="Offr home">
            <OffrLogo priority />
          </a>
          <a
            className="build-note"
            href="#waitlist"
            aria-label="Offr is launching soon. Join the waitlist for launch updates."
          >
            <CircleDot size={14} strokeWidth={2} aria-hidden="true" />
            <strong>Launching soon</strong>
          </a>
        </div>
        <button
          className="menu-button"
          type="button"
          aria-expanded={menuOpen}
          aria-controls="site-navigation"
          onClick={() => setMenuOpen((current) => !current)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          <span className="sr-only">{menuOpen ? "Close menu" : "Open menu"}</span>
        </button>
        <nav
          className={`site-nav ${menuOpen ? "is-open" : ""}`}
          id="site-navigation"
          aria-label="Primary navigation"
        >
          <a href="#how-it-works" onClick={closeMenu}>
            How it works
          </a>
          <a href="#what-we-offr" onClick={closeMenu}>
            What we Offr
          </a>
          <a href="#dashboard" onClick={closeMenu}>
            Dashboard
          </a>
          <a href="#faq" onClick={closeMenu}>
            FAQ
          </a>
          <span className="site-nav__coupon" aria-hidden="true">
            <TicketPercent size={16} strokeWidth={2} />
            <span>
              <strong>DISCOUNT COUPONS!</strong>
              <small>for early waitlist members</small>
            </span>
          </span>
          <a
            className="nav-cta"
            href="#waitlist"
            aria-label="Join waitlist — discount coupons for early waitlist members"
            onClick={closeMenu}
          >
            <span>Join waitlist</span>
          </a>
        </nav>
      </header>

      <button
        className={`nav-scrim ${menuOpen ? "is-open" : ""}`}
        type="button"
        aria-label="Close navigation"
        aria-hidden={!menuOpen}
        tabIndex={menuOpen ? 0 : -1}
        onClick={closeMenu}
      />

      <main>
        <section className="hero section-glow atmosphere atmosphere--hero is-inview" id="top" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <div className="hero__stage">
            <AmbientOrbit side="left" />
            <AmbientOrbit side="right" />
            <HeroApplicationStorm />
            <div className="hero__content page-width">
              <span className="hero__eyebrow">Your AI job-application agent</span>
              <h1>Apply to <span className="hero__ten-x">10×</span> more jobs in the same time.</h1>
              <p>
                Save your profile once. Every form after that fills itself —
                you just review and submit.
              </p>
              <WaitlistForm />
              <WaitlistTally />
            </div>
          </div>
        </section>

        <section className="problem section-shell atmosphere atmosphere--problem" id="problem" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <span className="section-shape section-shape--coral" aria-hidden="true" />
          <div className="page-width problem__layout">
            <div className="problem__copy" data-reveal="split-left">
              <span className="section-eyebrow">THE PROBLEM</span>
              <h2>Every application asks for the same facts again.</h2>
              <p className="problem__cost">
                Roughly 23 minutes each. About four hours for ten roles. Every
                time, from scratch.
              </p>
            </div>
            <div data-reveal="fan"><RepeatPain /></div>
          </div>
          <div className="page-width">
            <aside className="founder-note" data-reveal="split-left">
              <div className="founder-note__label">
                <span>Why we’re building <OffrLogo className="offr-logo--caption" /></span>
              </div>
              <div className="founder-note__body">
                <blockquote className="founder-note__quote">
                  <span className="founder-note__quote-text">
                    I watched friends do everything right and still lose months
                    to retyping the same form. That shouldn’t be the hard part.
                  </span>
                </blockquote>
              </div>
            </aside>
          </div>
        </section>

        <section className="solution-pause atmosphere atmosphere--solution" id="solution" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <div className="solution-pause__inner">
            <h2>How <OffrLogo className="offr-logo--section-mini" /> solves this.</h2>
            <SolutionReadingCopy />
          </div>
        </section>

        <section className="agent-story section-shell atmosphere atmosphere--story" id="how-it-works" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <span className="section-shape section-shape--violet" aria-hidden="true" />
          <div className="page-width">
            <div className="agent-story__copy">
              <h2 data-reveal="mask">How <OffrLogo className="offr-logo--inline" /> prepares an application in five steps.</h2>
              <p data-reveal="split-right">
                You choose the roles. Offr prepares each application. Nothing
                submits without you.
              </p>
            </div>
            <AgentDemo />
          </div>
        </section>

        <section className="value-section section-shell atmosphere atmosphere--value" id="what-we-offr" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <span className="section-shape section-shape--mint" aria-hidden="true" />
          <div className="page-width value-section__inner">
            <header className="value-section__intro">
              <h2 data-reveal="mask">What we <OffrLogo className="offr-logo--section-heading" />.</h2>
              <p data-reveal="split-right">Fast enough for volume. Careful enough to trust.</p>
            </header>

            <div className="value-proof" data-reveal="cascade">
              <div className="value-proof__comparison">
                <div className="value-proof__number value-proof__number--manual">
                  <strong>≈23 min</strong>
                  <span>BY HAND</span>
                </div>
                <span className="application-tax__arrow" aria-hidden="true">→</span>
                <div className="value-proof__number value-proof__number--target">
                  <strong>&lt;60 sec</strong>
                  <span>WITH OFFR</span>
                  <span className="tax-metric__celebration" aria-hidden="true">
                    <i /><i /><i /><i /><i /><i /><i /><i />
                  </span>
                </div>
              </div>
              <p className="value-proof__caption">Your active time per application, with Offr.</p>
              <p className="value-proof__source">
                The 23-minute figure is a directional industry benchmark.{" "}
                <a href="https://www.linkedin.com/pulse/job-application-process-facts-figures-albert-robescu-ewq7e/" target="_blank" rel="noreferrer">
                  Read the cited benchmark
                </a>.
              </p>
            </div>

            <ul className="advantage-grid" aria-label="Advantages of Offr">
              {advantages.map((advantage) => (
                <li key={advantage.title}>
                  <h3>
                    {advantage.title === "Offr asks instead of guessing" ? (
                      <><OffrLogo className="offr-logo--advantage" /> asks instead of guessing</>
                    ) : advantage.title}
                  </h3>
                  <p>{advantage.copy}</p>
                </li>
              ))}
            </ul>
          </div>

        </section>

        <div className="section-bridge section-bridge--portals" data-scroll-zone>
          <PortalRail />
        </div>

        <section className="dashboard-section section-shell atmosphere atmosphere--dashboard" id="dashboard" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <span className="section-shape section-shape--violet" aria-hidden="true" />
          <AmbientOrbit side="left" />
          <div className="page-width">
            <div className="dashboard-section__copy">
              <h2 data-reveal="split-left">Every application <OffrLogo className="offr-logo--section-heading" /> sends, tracked in one place.</h2>
              <p data-reveal="split-right">
                See what’s queued, ready for review, submitted, or waiting on you.
              </p>
            </div>
            <div className="dashboard-showcase" data-reveal="depth">
              <DashboardPreview />
              <DashboardExtensionPreview />
            </div>
          </div>
        </section>

        <section className="early-access trust-cta section-shell atmosphere atmosphere--waitlist" id="waitlist" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <AmbientOrbit side="right" />
          <span className="section-shape section-shape--coral" aria-hidden="true" />
          <div className="page-width trust-cta__inner">
            <div className="trust-cta__copy" data-reveal="split-left">
              <h2>Be first in line.</h2>
              <p>
                Choose the jobs. Offr prepares each application and waits for your review.
              </p>
            </div>
            <div className="trust-cta__join" data-reveal="split-right">
              <div className="waitlist-perk" role="note">
                <span className="waitlist-perk__icon" aria-hidden="true">
                  <TicketPercent size={20} strokeWidth={1.9} />
                </span>
                <span className="waitlist-perk__copy">
                  <strong>DISCOUNT COUPONS!</strong>
                  <small>for early waitlist members</small>
                </span>
                <em>Join early</em>
              </div>
              <OffrLogo markOnly />
              <h3>Join the waitlist.</h3>
              <p>
                Get early access and founding-member pricing when we launch.
              </p>
              <WaitlistForm variant="full" />
              <WaitlistTally compact />
            </div>
          </div>
        </section>

        <section className="faq section-shell atmosphere atmosphere--faq" id="faq" data-scroll-zone>
          <span className="atmosphere__field" aria-hidden="true" />
          <div className="page-width faq__layout">
            <div data-reveal="split-left">
              <h2>What to know<br />before joining.</h2>
              <a href="#waitlist">
                Save my early-access spot
                <ArrowRight size={17} aria-hidden="true" />
              </a>
            </div>
            <div className="faq__list" data-reveal="cascade">
              {faqs.map((item, index) => (
                <details key={item.question} open={index === 0}>
                  <summary>
                    {item.question}
                    <ChevronDown size={20} aria-hidden="true" />
                  </summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>

      <ScrollToTop />

      <footer id="site-footer">
        <div className="page-width footer__inner">
          <div className="footer__identity">
            <a className="brand" href="#top" aria-label="Offr home">
              <OffrLogo />
            </a>
            <p>
              An AI job-application agent. You choose the roles; Offr prepares
              each one and waits for your review.
            </p>
            <span className="footer__status">
              <CircleDot size={13} strokeWidth={2.2} aria-hidden="true" />
              In development · not yet live
            </span>
          </div>

          <nav className="footer__nav" aria-label="Product links">
            <strong>Product</strong>
            <a href="#how-it-works">How it works</a>
            <a href="#what-we-offr">What we Offr</a>
            <a href="#dashboard">Dashboard</a>
            <a href="#waitlist">Join the waitlist</a>
          </nav>

          <nav className="footer__nav" aria-label="More information">
            <strong>More</strong>
            <a href="#faq">FAQ</a>
            <a href="#privacy-note">Privacy</a>
            <a href="#terms-note">Pre-launch terms</a>
            <span title="Contact details will be published before launch">Contact at launch</span>
          </nav>
        </div>

        <div className="page-width footer__legal">
          <span>© 2026 Offr. All rights reserved.</span>
          <div>
            <p id="privacy-note">
              Waitlist details are used for launch updates and early-access communication.
            </p>
            <p id="terms-note">
              Product imagery is a synthetic preview of software in development. Applicant
              tracking systems named are trademarks of their respective owners; Offr is not
              affiliated with or endorsed by them.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
